import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../Utils/api';
import styles from './userSuggestions.module.css';
import { useAllUsers } from '../hooks/useUsers';
import { useAllFriends, useFollowers, useFollowing } from '../hooks/useFriends';
import { getNotif } from '../Utils/GetNotif';

const UserSuggestions = ({ userId, token, refresh }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [processingIds, setProcessingIds] = useState(new Set());
  const navigate = useNavigate();

  const { data: allUsers = [], isLoading: usersLoading, error, refetch: refetchUsers } = useAllUsers(token);
  const { data: followingList = [], isLoading: followingLoading, refetch: refetchFollowing } = useFollowing(token);
  const { data: followersList = [], isLoading: followersLoading, refetch: refetchFollowers } = useFollowers(token);
  const { data: allFriendsList = [], isLoading: allFriendsLoading, refetch: refetchAllFriends } = useAllFriends(token);

  useEffect(() => {
    if (refresh) {
      refetchUsers();
      refetchFollowing();
      refetchFollowers();
      refetchAllFriends();
    }
  }, [refresh, refetchUsers, refetchFollowing, refetchAllFriends, refetchFollowers]);

  useEffect(() => {
    if (!usersLoading && !followingLoading && !followersLoading && !allFriendsLoading && allUsers.length > 0) {
      const followingIds = new Set(followingList.map(u => u._id || u.id));
      const followersIds = new Set(followersList.map(u => u._id || u.id));
      const friendsIds = new Set(allFriendsList.map(u => u._id || u.id));
      let filtered = allUsers.filter(u => {
        const uid = u._id || u.id;
        if (!uid) return false;
        if (uid === userId) return false; 
        if (followingIds.has(uid)) return false; 
        if (followersIds.has(uid)) return false; 
        if (friendsIds.has(uid)) return false; 
        return true;
      });

      // Mixer la liste et prendre 5 au hasard
      filtered = filtered.sort(() => 0.5 - Math.random()).slice(0, 5);
      setSuggestions(filtered);
    }
  }, [usersLoading, followingLoading, allUsers, followingList, userId]);

  const loading = usersLoading || followingLoading;

  const handleFollow = async (e, userToFollow) => {
    e.stopPropagation();
    const uid = userToFollow._id || userToFollow.id;
    if (processingIds.has(uid)) return;
    setProcessingIds(prev => new Set(prev).add(uid));
    try {
      await axios.put(`${API_URL}/follow`, { authorId: uid }, {
        headers: { Authorization: `Bearer${token}` }
      });
      // Envoyer la notification via socket
      getNotif({ userId, authorId: uid, message: "a commencé à vous suivre", type: 'follow', token });
      // Retirer de la liste
      setSuggestions(prev => prev.filter(u => (u._id || u.id) !== uid));
    } catch (error) {
      console.error(error);
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(uid);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <aside className={styles.suggestionsContainer}>
        <h3 className={styles.title}>Suggestions pour vous</h3>
        <div className={styles.loadingSkeleton}>
          {[1, 2, 3].map(i => (
            <div key={i} className={styles.skeletonItem}>
              <div className={styles.skeletonAvatar} />
              <div className={styles.skeletonLines}>
                <div className={styles.skeletonLine} />
                <div className={styles.skeletonLine} style={{ width: '60%' }} />
              </div>
            </div>
          ))}
        </div>
      </aside>
    );
  }

  if (error) {
    return (
      <aside className={styles.suggestionsContainer}>
        <h3 className={styles.title}>Suggestions</h3>
        <p className={styles.emptyText}>Erreur de chargement: {error}</p>
      </aside>
    );
  }

  if (suggestions.length === 0) {
    return (
      <aside className={styles.suggestionsContainer}>
        <h3 className={styles.title}>Suggestions</h3>
        <p className={styles.emptyText}>Aucun utilisateur à suggérer pour le moment.</p>
      </aside>
    );
  }

  return (
    <aside className={styles.suggestionsContainer}>
      <h3 className={styles.title}>Suggestions</h3>
      <div className={styles.list}>
        {suggestions.map((user, index) => {
          const uid = user._id || user.id;
          const isProcessing = processingIds.has(uid);
          return (
            <div 
              key={`${uid}-${index}`} 
              className={styles.userCard}
              onClick={() => navigate(`/profile/${uid}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/profile/${uid}`); }}
            >
              {user.userPP || user.pp ? (
                <img 
                  src={user.userPP || user.pp} 
                  alt={user.username} 
                  className={styles.avatar}
                />
              ) : (
                <div className={styles.avatarPlaceholder}>
                  {user.username?.charAt(0).toUpperCase()}
                </div>
              )}
              <div className={styles.textDetails}>
                <p className={styles.displayName}>{user.displayName || user.username}</p>
                <p className={styles.handle}>@{user.username}</p>
                {user.mutualFriends > 0 ? (
                   <p className={styles.mutualFriends}>{user.mutualFriends} ami(s) en commun</p>
                ) : null}
              </div>
              <button 
                className={`${styles.followBtn} ${isProcessing ? styles.processing : ''}`}
                onClick={(e) => handleFollow(e, user)}
                disabled={isProcessing}
              >
                {isProcessing ? '...' : 'Suivre'}
              </button>
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default UserSuggestions;
